import type { GameState } from './types';
import { cloneState, applyMove } from './state';
import { getLegalMoves, checkMotherLoss, checkGameOver } from './rules';

// ─── Scoring ─────────────────────────────────────────────────────────────────

function scorePosition(state: GameState): number {
  const { points } = state;
  let score = 0;

  // ── a) Trapped enemy (white) pieces ──────────────────────────────────────
  let trappedCount = 0;
  for (let i = 1; i <= 24; i++) {
    const pt = points[i]!;
    if (pt.topOwner === 'black' && pt.pinnedOwner === 'white') {
      trappedCount++;
      score += trappedCount === 1 ? 100 : trappedCount === 2 ? 50 : 10;
    }
  }

  // ── b) Vulnerable solo black pieces (probability-based) ──────────────────
  for (let y = 1; y <= 24; y++) {
    const pt = points[y]!;
    if (pt.topOwner !== 'black' || pt.topCount !== 1) continue;
    if (pt.pinnedOwner === 'white') continue; // white already pinned here; can't double-pin

    // Count die values d ∈ {1..6} where white has a movable (top-stack) piece at y+d.
    // White moves high→low: a piece at y+d reaches y by rolling d.
    let blockingDice = 0;
    for (let d = 1; d <= 6; d++) {
      if (y + d <= 24 && points[y + d]!.topOwner === 'white') blockingDice++;
    }
    if (blockingDice === 0) continue;

    // P(at least one of 2 independent dice is a blocking value)
    const p = 1 - ((6 - blockingDice) / 6) ** 2;
    score += -25 * 2 * p;
  }

  // ── c) Pip-distance differential ─────────────────────────────────────────
  const blackDists: number[] = [];
  for (let i = 1; i <= 24; i++) {
    const pt = points[i]!;
    const blackDist = 25 - i;   // pips for black to bear off from i
    const whiteDist = i;        // pips for white to bear off from i

    if (pt.topOwner === 'black') {
      score -= pt.topCount * blackDist;
      for (let k = 0; k < pt.topCount; k++) blackDists.push(blackDist);
    }
    if (pt.pinnedOwner === 'black') { score -= blackDist; blackDists.push(blackDist); }
    if (pt.topOwner === 'white') score += pt.topCount * whiteDist;
    if (pt.pinnedOwner === 'white') score += whiteDist;
  }

  // ── d) Cohesion — penalise spread-out black pieces ───────────────────────
  const COHESION_WEIGHT = 3;
  if (blackDists.length > 1) {
    const mean = blackDists.reduce((a, b) => a + b, 0) / blackDists.length;
    const variance = blackDists.reduce((s, d) => s + (d - mean) ** 2, 0) / blackDists.length;
    score -= COHESION_WEIGHT * variance;
  }

  return score;
}

// ─── Search ──────────────────────────────────────────────────────────────────

interface SearchResult {
  states: GameState[];   // state snapshots after each move, for animation
  score: number;
}

function exploreAll(state: GameState): SearchResult[] {
  const moves = getLegalMoves(state);
  if (moves.length === 0) {
    return [{ states: [], score: scorePosition(state) }];
  }

  const results: SearchResult[] = [];
  const seen = new Set<string>();

  for (const move of moves) {
    const key = `${move.from}-${move.to}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const next = applyMove(state, move.from, move.to, move.dieIndex);

    const motherResult = checkMotherLoss(next, 'black');
    if (motherResult !== null) {
      next.motherOutcome = motherResult;
      const score = motherResult === 'white-loses' ? 99999
                  : motherResult === 'tie'         ? 0
                  :                                  -99999;
      results.push({ states: [cloneState(next)], score });
      continue;
    }

    const over = checkGameOver(next);
    if (over !== null) {
      next.winner = over.winner;
      next.phase = 'gameover';
      const score = over.winner === 'black' ? 99999 : -99999;
      results.push({ states: [cloneState(next)], score });
      continue;
    }

    for (const sub of exploreAll(next)) {
      results.push({ states: [cloneState(next), ...sub.states], score: sub.score });
    }
  }

  return results;
}

// ─── Public API ──────────────────────────────────────────────────────────────

export function buildAiTurnStates(state: GameState, dice: number[]): GameState[] {
  const initial = cloneState(state);
  initial.dice = dice;
  initial.diceUsed = dice.map(() => false);
  initial.phase = 'ai';

  const snapshots: GameState[] = [cloneState(initial)];

  const allPaths = exploreAll(initial);
  if (allPaths.length === 0) return snapshots;

  const bestScore = Math.max(...allPaths.map(p => p.score));
  const bestPaths = allPaths.filter(p => p.score === bestScore);
  const chosen = bestPaths[Math.floor(Math.random() * bestPaths.length)]!;

  return [...snapshots, ...chosen.states];
}
